class AkoGiftButtons extends HTMLElement {
  connectedCallback() {
    this.checkbox = this.querySelector('#has-gift-card');
    this.messageWrap = this.querySelector('.gift-message-wrapper');
    this.textarea = this.querySelector('#gift-message');
    this.counter = this.querySelector('#char-count');
    this.errorText = this.querySelector('.gift-message-error');
    this.giftButton = this.querySelector('.addGiftButton');
    this.form = this.querySelector('form');
    if (!this.checkbox || !this.form) return;

    this.submitButton = this.form.querySelector('[type="submit"]');
    this.errorsContainer = this.form.querySelector('[data-cart-errors-container]');
    this.maxLength = this.textarea ? parseInt(this.textarea.getAttribute('maxlength'), 10) || 0 : 0;
    this.giftVariantId = this.giftButton ? this.giftButton.getAttribute('data-variant-id') : null;
    this.relatedTitle = this.giftButton ? this.giftButton.getAttribute('related-product-title') : '';

    this.submitAttempted = false;

    this.checkbox.addEventListener('change', this.onToggle.bind(this));
    if (this.textarea) this.textarea.addEventListener('input', this.onInput.bind(this));
    this.addEventListener('submit', this.onSubmit.bind(this), { capture: true });

    // Re-apply our disabled state if something external (variant swap) clears it
    if (this.submitButton) {
      this.buttonObserver = new MutationObserver(this.reconcileButton.bind(this));
      this.buttonObserver.observe(this.submitButton, { attributes: true, attributeFilter: ['disabled'] });
    }

    this.onToggle();
    this.onInput();
  }

  disconnectedCallback() {
    this.buttonObserver?.disconnect();
  }

  onToggle() {
    if (this.messageWrap) {
      this.messageWrap.style.display = this.checkbox.checked ? '' : 'none';
    }
    this.updateValidity();
  }

  onInput() {
    if (this.textarea && this.counter) {
      this.counter.textContent = this.textarea.value.length + ' / ' + this.maxLength;
    }
    this.updateValidity();
  }

  isMessageValid() {
    const message = this.textarea ? this.textarea.value.trim() : '';
    const hasEmoji = /[\p{Emoji_Presentation}\p{Extended_Pictographic}]/gu.test(message);
    return Boolean(message) && !hasEmoji;
  }

  shouldBlock() {
    return this.submitAttempted && this.checkbox.checked && !this.isMessageValid();
  }

  updateValidity() {
    const block = this.shouldBlock();
    if (this.textarea) this.textarea.classList.toggle('input-error', block);
    if (this.errorText) this.errorText.hidden = !block;
    if (!this.submitButton) return;

    this.isReconciling = true;
    if (block) {
      this.submitButton.setAttribute('disabled', 'disabled');
      this.submitButton.dataset.giftDisabled = 'true';
    } else if (this.submitButton.dataset.giftDisabled === 'true') {
      this.submitButton.removeAttribute('disabled');
      delete this.submitButton.dataset.giftDisabled;
    }
    this.isReconciling = false;
  }

  reconcileButton() {
    if (this.isReconciling || !this.submitButton) return;
    if (this.shouldBlock() && !this.submitButton.hasAttribute('disabled')) {
      this.isReconciling = true;
      this.submitButton.setAttribute('disabled', 'disabled');
      this.submitButton.dataset.giftDisabled = 'true';
      this.isReconciling = false;
    }
  }

  onSubmit(event) {
    if (!this.checkbox.checked || !this.giftVariantId) return;

    event.preventDefault();
    event.stopImmediatePropagation();

    if (!this.isMessageValid()) {
      this.submitAttempted = true;
      this.updateValidity();
      if (this.textarea) this.textarea.focus();
      return;
    }

    const formData = new FormData(this.form);
    const mainId = parseInt(formData.get('id'), 10);
    const giftId = parseInt(this.giftVariantId, 10);

    if (!Number.isFinite(mainId) || !Number.isFinite(giftId)) {
      console.error('[ako-gift] invalid variant id', { mainId, giftId });
      this.showError('Unable to add the gift product. Please refresh and try again.');
      return;
    }

    const message = this.textarea.value.trim();

    const mainProperties = {};
    for (const [key, value] of formData.entries()) {
      const match = key.match(/^properties\[(.+)\]$/);
      if (!match) continue;
      const name = match[1];
      if (Object.prototype.hasOwnProperty.call(mainProperties, name)) {
        const existing = mainProperties[name];
        mainProperties[name] = Array.isArray(existing) ? existing.concat(value) : [existing, value];
      } else {
        mainProperties[name] = value;
      }
    }

    const mainItem = {
      id: mainId,
      quantity: parseInt(formData.get('quantity') || '1', 10),
    };
    if (Object.keys(mainProperties).length) mainItem.properties = mainProperties;

    const sellingPlan = formData.get('selling_plan');
    if (sellingPlan) mainItem.selling_plan = sellingPlan;

    const items = [
      mainItem,
      {
        id: giftId,
        quantity: 1,
        properties: {
          '卡片主商品': this.relatedTitle || '',
          '卡片內容': message,
        },
      },
    ];

    const url = window.Shopify && window.Shopify.routes && window.Shopify.routes.root
      ? window.Shopify.routes.root + 'cart/add.js'
      : '/cart/add.js';

    this.toggleSubmitting(true);
    this.clearError();

    fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
      body: JSON.stringify({ items: items }),
    })
      .then((res) => res.json().then((body) => ({ ok: res.ok, body: body })))
      .then((result) => {
        if (!result.ok) {
          throw result.body;
        }
        this.openMiniCartOrRedirect();
      })
      .catch((err) => {
        console.error('[ako-gift] add to cart failed', err);
        const description = (err && (err.description || err.message)) || 'Unable to add to cart.';
        this.showError(description);
        this.toggleSubmitting(false);
      });
  }

  openMiniCartOrRedirect() {
    const hasCartDrawer = !!document.querySelector('cart-drawer');
    if (hasCartDrawer) {
      document.dispatchEvent(new CustomEvent('theme:cart:refresh', { bubbles: true }));
      document.dispatchEvent(new CustomEvent('theme:product:added', { bubbles: true }));
      this.toggleSubmitting(false);
      return;
    }
    const root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
    window.location.href = root + 'cart';
  }

  toggleSubmitting(isSubmitting) {
    if (!this.submitButton) return;
    this.submitButton.toggleAttribute('disabled', isSubmitting);
    this.submitButton.classList.toggle('loading', isSubmitting);
  }

  showError(message) {
    if (!this.errorsContainer) {
      window.alert(message);
      return;
    }
    this.errorsContainer.textContent = message;
    this.errorsContainer.style.display = '';
  }

  clearError() {
    if (!this.errorsContainer) return;
    this.errorsContainer.textContent = '';
  }
}

if (!customElements.get('ako-gift-buttons')) {
  customElements.define('ako-gift-buttons', AkoGiftButtons);
}
